import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useSessionStore } from '@/state/sessionStore';
import { playSound } from '@/sound/audioSystem';
import { eventBus } from '@/events/eventBus';
import { actorLabel, currentActorId } from '@/domain/actor';
import { routeInboundCall } from '@/domain/sweeperOutcomes';
import { pickStudioAgent } from '@/domain/studioAgent';
import type {
  ActivityFeedEntry,
  ConversationEntry,
  StudioId,
} from '@/domain/types';

// Outbound + inbound call orchestration. Mock simulation of the PRD §15.1/15.2 flow.
// All "real" services (Twilio, Dialpad, JustCall) are simulated by timers + brain mutations.

export interface ActiveCallContext {
  id: string;
  customerId: string;
  studioId: StudioId;
  direction: 'inbound' | 'outbound';
  startedAt: string;
  transcript: TranscriptLine[];
  status: 'connecting' | 'ringing' | 'live' | 'wrapping' | 'completed';
}

export interface TranscriptLine {
  id: string;
  speaker: 'customer' | 'agent';
  body: string;
  atIso: string;
}

const listeners = new Set<(c: ActiveCallContext | null) => void>();
let activeCall: ActiveCallContext | null = null;

export function getActiveCall() {
  return activeCall;
}

export function onActiveCallChange(handler: (c: ActiveCallContext | null) => void) {
  listeners.add(handler);
  return () => {
    listeners.delete(handler);
  };
}

function notifyListeners() {
  for (const l of listeners) {
    try {
      l(activeCall);
    } catch (err) {
      console.error('[callFlow] listener error', err);
    }
  }
}

function nowIso() {
  return useSimulationStore.getState().clockIso;
}

function appendActivity(a: Omit<ActivityFeedEntry, 'id' | 'timestamp'>) {
  useBrainStore.getState().appendActivity({
    ...a,
    id: `act-call-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    timestamp: nowIso(),
  });
}

const SARAH_SCRIPT: TranscriptLine[] = [
  { id: 't1', speaker: 'agent', body: 'Hi Sarah, it\'s Lydia from Tattoo Vault Coburg.', atIso: '' },
  { id: 't2', speaker: 'customer', body: 'Hey, glad you got back to me. Sorry about the early hours yesterday.', atIso: '' },
  {
    id: 't3',
    speaker: 'agent',
    body: 'No worries at all. I\'ve got your Saturday slot with Pat still here. Half-day, $400 deposit.',
    atIso: '',
  },
  {
    id: 't4',
    speaker: 'customer',
    body: 'Yeah let\'s lock it in. Can you send me the link to pay now?',
    atIso: '',
  },
  {
    id: 't5',
    speaker: 'agent',
    body: 'Sending it through now. See you Saturday at 11.',
    atIso: '',
  },
];

const GENERIC_OUTBOUND_SCRIPT: TranscriptLine[] = [
  { id: 't1', speaker: 'agent', body: 'Hi, it\'s the team from Tattoo Vault.', atIso: '' },
  { id: 't2', speaker: 'customer', body: 'Hey, thanks for calling back.', atIso: '' },
  { id: 't3', speaker: 'agent', body: 'I have your record open here. Want me to talk you through next steps?', atIso: '' },
];

export function startOutboundCall(opts: { customerId: string; studioId: StudioId }) {
  const id = `call-${Date.now()}`;
  activeCall = {
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    direction: 'outbound',
    startedAt: nowIso(),
    transcript: [],
    status: 'connecting',
  };
  notifyListeners();
  playSound('inbound-ring');
  setTimeout(() => connectCall(opts.customerId === 'sarah-holman' ? SARAH_SCRIPT : GENERIC_OUTBOUND_SCRIPT), 1800);
  return id;
}

export function answerInboundCall(callId: string) {
  if (!activeCall || activeCall.id !== callId) {
    // A routed callback (B7) that rang in while someone else was signed in here
    // has no live call yet: arm it from the ringing notification, then connect.
    const ringing = useUIStore.getState().incomingCalls.find((c) => c.id === callId);
    if (!ringing) return;
    activeCall = {
      id: ringing.id,
      customerId: ringing.customerId,
      studioId: ringing.studioId as StudioId,
      direction: 'inbound',
      startedAt: ringing.startedAt,
      transcript: [],
      status: 'ringing',
    };
  }
  connectCall(SARAH_SCRIPT);
}

function connectCall(script: TranscriptLine[]) {
  if (!activeCall) return;
  activeCall = { ...activeCall, status: 'live' };
  notifyListeners();
  appendActivity({
    type: 'outbound-call',
    customerId: activeCall.customerId,
    studioId: activeCall.studioId,
    body: `Outbound call connected to ${activeCall.customerId}.`,
  });
  // Transcript streams into activeCall.transcript only. The LiveCallBanner
  // renders it live. On hang-up we consolidate into ONE conversation entry.
  // Cadence tuned for the live demo: ~2.4s between utterances feels
  // conversational, slow enough to read along, fast enough not to drag.
  const UTTERANCE_INTERVAL_MS = 2400;
  script.forEach((line, idx) => {
    setTimeout(() => {
      if (!activeCall) return;
      const atIso = nowIso();
      const lineId = `${line.id}-${activeCall.id}`;
      activeCall = {
        ...activeCall,
        transcript: [...activeCall.transcript, { ...line, id: lineId, atIso }],
      };
      notifyListeners();
    }, idx * UTTERANCE_INTERVAL_MS);
  });
}

export function endCall(disposition: {
  tags: string[];
  notes?: string;
}) {
  if (!activeCall) return;
  const finished: ActiveCallContext = { ...activeCall, status: 'completed' };
  const endIso = nowIso();
  const durationSec = Math.max(
    1,
    Math.round((new Date(endIso).getTime() - new Date(finished.startedAt).getTime()) / 1000),
  );
  const brain = useBrainStore.getState();

  // Resolve the operator on the call so per-agent call reporting (Prompt F) has a
  // durable, real attribution instead of a hardcoded name.
  const agentId = useSessionStore.getState().agentId ?? undefined;
  const agent = agentId ? brain.agents[agentId] : undefined;
  const authorName = agent ? `${agent.firstName} ${agent.lastName}` : 'Lydia';

  const dispoBits = [
    disposition.tags.length > 0 ? `Disposition: ${disposition.tags.join(', ')}` : null,
    disposition.notes,
  ]
    .filter(Boolean)
    .join(' · ');

  const entry: ConversationEntry = {
    id: `convo-call-${finished.id}`,
    customerId: finished.customerId,
    channel: 'voice',
    direction: finished.direction === 'inbound' ? 'in' : 'out',
    body: `${finished.direction === 'inbound' ? 'Inbound' : 'Outbound'} call · ${durationSec}s · ${finished.transcript.length} utterances${dispoBits ? ` · ${dispoBits}` : ''}`,
    timestamp: endIso,
    authorRole: 'agent',
    authorName,
    ...(agentId ? { agentId } : {}),
    transcript: finished.transcript.map((t) => ({
      speaker: t.speaker,
      body: t.body,
      atIso: t.atIso,
    })),
  };
  brain.addConversationEntry(finished.customerId, entry);

  appendActivity({
    type: 'call-completed',
    customerId: finished.customerId,
    studioId: finished.studioId,
    body: `Call complete. ${dispoBits || 'No disposition'}.`,
  });
  eventBus.publish('call:completed', { customerId: finished.customerId });
  activeCall = null;
  notifyListeners();
}

/**
 * A client calls in (simulated). B7 (75nh D08-26; Anas 1 Oct Q3): if a Sweeper
 * called them in the last 7 days and nobody else has reached them since, the
 * call routes to that Sweeper — it rings only when they're the one signed in
 * here (IncomingCallOverlay), so another workspace isn't rung.
 *
 * L5 (D05-05): otherwise it rings the studio's own agent. A bookings agent
 * signed in here who covers the called studio gets it; a call to any other
 * studio is routed to that studio's agent and doesn't ring here ("INBOUND TO
 * BRISBANE" no longer rings Coburg's agent). L5 (D05-06): a call nobody answers
 * within RING_TIMEOUT_MS is a missed call on the record.
 */
export function dispatchInboundCall(opts: { customerId: string; studioId: StudioId }) {
  const id = `inbound-${Date.now()}`;
  const brain = useBrainStore.getState();
  const callback = routeInboundCall({
    customer: brain.customers[opts.customerId],
    outcomes: brain.sweeperOutcomes,
    nowIso: nowIso(),
  });
  const studioAgent = callback || signedInAgentCovers(opts.studioId) ? undefined : studioAgentFor(opts.studioId);
  const route = callback
    ? { pid: callback.pid, kind: 'callback' as const }
    : studioAgent
      ? { pid: studioAgent, kind: 'studio' as const }
      : undefined;
  useUIStore.getState().addIncomingCall({
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    startedAt: nowIso(),
    ...(route ? { routedToPid: route.pid, routedToName: actorLabel(route.pid), routeKind: route.kind } : {}),
  });
  // Nobody picks up in time: a missed call (+ voicemail) on the record.
  setTimeout(() => {
    const call = useUIStore.getState().incomingCalls.find((c) => c.id === id);
    if (call?.status !== 'ringing') return;
    recordMissedCall(id, 'not-answered');
    useUIStore.getState().setIncomingCallStatus(id, 'declined');
    if (activeCall?.id === id && activeCall.status === 'ringing') {
      activeCall = null;
      notifyListeners();
    }
  }, RING_TIMEOUT_MS);
  if (route && route.pid !== currentActorId()) return; // someone else's phone: no ring here
  activeCall = {
    id,
    customerId: opts.customerId,
    studioId: opts.studioId,
    direction: 'inbound',
    startedAt: nowIso(),
    transcript: [],
    status: 'ringing',
  };
  playSound('inbound-ring');
  notifyListeners();
}

export function declineInboundCall(callId: string) {
  // L5 (D05-06): a declined call is a missed call on the studio agent's list.
  recordMissedCall(callId, 'declined');
  if (!activeCall || activeCall.id !== callId) return;
  activeCall = null;
  notifyListeners();
}

/** A ringing call nobody answers within this window is a missed call (L5, D05-06). */
export const RING_TIMEOUT_MS = 45_000;

/**
 * L5 (D05-05): the studio's own bookings agent — one whose primary studio this
 * is, else one who covers it. Studio managers and Ops aren't on the phones.
 */
export function studioAgentFor(studioId: StudioId): string | undefined {
  return pickStudioAgent(Object.values(useBrainStore.getState().agents), studioId)?.id;
}

/** The signed-in person is a bookings agent who covers this studio's phones. */
function signedInAgentCovers(studioId: StudioId): boolean {
  const agentId = useSessionStore.getState().agentId;
  const agent = agentId ? useBrainStore.getState().agents[agentId] : undefined;
  return !!agent && agent.role === 'bookings-agent' && (agent.primaryStudio === studioId || !!agent.assignedStudios?.includes(studioId));
}

/** The simulated voicemail a caller leaves when nobody picks up. */
function voicemailFor(customerId: string): string {
  const first = useBrainStore.getState().customers[customerId]?.firstName?.trim();
  return `Hi${first && !first.startsWith('@') ? `, it's ${first}` : ''} — calling about booking in. Please call me back.`;
}

/**
 * L5 (D05-06): a ringing call that was declined, or not answered in time, leaves
 * a missed call (with the voicemail) on the customer's record — an inbound voice
 * entry, saved with the customer like an answered call's. The Agent's list
 * surfaces it as MISSED CALL (queueEngine's missed-call rule, unchanged).
 * Idempotent per call: a decline and the ring timeout never record it twice, and
 * an answered call never records one.
 */
export function recordMissedCall(callId: string, reason: 'declined' | 'not-answered'): void {
  const ringing = useUIStore.getState().incomingCalls.find((c) => c.id === callId);
  const customerId = ringing?.customerId ?? (activeCall?.id === callId ? activeCall.customerId : undefined);
  if (!customerId) return;
  if (ringing?.status === 'answered') return;
  if (activeCall?.id === callId && activeCall.status !== 'ringing') return;
  const brain = useBrainStore.getState();
  const id = `convo-missed-${callId}`;
  if (brain.customers[customerId]?.conversation.some((e) => e.id === id)) return;
  const rang = ringing?.routedToName ? ` · rang ${ringing.routedToName}` : '';
  brain.addConversationEntry(customerId, {
    id,
    customerId,
    channel: 'voice',
    direction: 'in',
    body: `Missed call · ${reason === 'declined' ? 'declined' : 'not answered'}${rang} · Voicemail: "${voicemailFor(customerId)}"`,
    timestamp: nowIso(),
    authorRole: 'customer',
  });
  appendActivity({
    type: 'voicemail',
    customerId,
    studioId: (ringing?.studioId ?? activeCall?.studioId ?? 'coburg') as StudioId,
    body: `Missed call from ${brain.customers[customerId]?.firstName ?? 'a client'} · voicemail left · on the Agent's list.`,
  });
}
